import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListChecks, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { TASK_CATEGORIES, TEAM_CATEGORIES } from "@/lib/tasks";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import PageHelp from "@/components/PageHelp";
import TaskKpis from "@/components/tasks/TaskKpis";
import TaskFilters from "@/components/tasks/TaskFilters";
import TaskList from "@/components/tasks/TaskList";
import TaskDetailDialog from "@/components/tasks/TaskDetailDialog";
import TeamSummary from "@/components/tasks/TeamSummary";

const DEFAULTS = { category: "open", sort: "due", q: "", project_id: "", floor: "", priority: "", status: "", type: "", assignee_id: "", date_from: "", date_to: "", done_days: 30 };
const PAGE_SIZE = 50;
const PREF_KEYS = ["category", "sort", "project_id", "priority", "type", "status", "floor", "done_days"];

export default function MyTasks() {
  const { user } = useAuth();
  const isManager = can(user, "tasks:team");
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("mine");
  const [f, setF] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [team, setTeam] = useState(null);
  const [openRef, setOpenRef] = useState(null);
  const prefTimer = useRef(null);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    if (isManager) api.get("/tasks/assignees").then((r) => setMembers(r.data)).catch(() => {});
    api.get("/tasks/prefs").catch(() => ({ data: {} })).then((r) => {
      const fromUrl = {};
      if (params.get("cat")) fromUrl.category = params.get("cat");
      if (params.get("priority")) fromUrl.priority = params.get("priority");
      const saved = Object.keys(fromUrl).length ? {} : r.data || {};
      const { tab: savedTab, ...savedFilters } = saved;
      setF({ ...DEFAULTS, ...savedFilters, ...fromUrl });
      if (savedTab === "team" && isManager) setTab("team");
    });
    const o = params.get("open");
    if (o && o.includes(":")) { const [type, id] = o.split(":"); setOpenRef({ type, id }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    if (!f) return;
    setLoading(true);
    const p = { scope: tab, page, page_size: PAGE_SIZE };
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v != null) p[k] = v; });
    api.get("/tasks", { params: p })
      .then((r) => { setData(r.data); setError(""); })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Aufgaben konnten nicht geladen werden."))
      .finally(() => setLoading(false));
    if (tab === "team") api.get("/tasks/team-summary").then((r) => setTeam(r.data)).catch(() => {});
  }, [f, tab, page]);

  useEffect(() => {
    const t = setTimeout(load, f?.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, f?.q]);

  const set = (patch) => {
    setPage(1);
    setF((prev) => {
      const next = { ...prev, ...patch };
      clearTimeout(prefTimer.current);
      prefTimer.current = setTimeout(() => {
        const v = { tab }; PREF_KEYS.forEach((k) => { v[k] = next[k]; });
        api.put("/tasks/prefs", v).catch(() => {});
      }, 800);
      return next;
    });
  };
  const switchTab = (v) => {
    setTab(v); setPage(1);
    setF((prev) => ({ ...prev, category: "open", assignee_id: "" }));
    api.put("/tasks/prefs", { ...Object.fromEntries(PREF_KEYS.map((k) => [k, f?.[k]])), category: "open", tab: v }).catch(() => {});
  };
  const closeDetail = () => {
    setOpenRef(null);
    if (params.get("open")) { params.delete("open"); setParams(params, { replace: true }); }
  };

  useEffect(() => {
    if (f?.project_id && projects.length && !projects.some((p) => p.id === f.project_id)) { set({ project_id: "" }); }
  }, [projects, f?.project_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (f?.floor && data?.floors && !data.floors.includes(f.floor)) { set({ floor: "" }); }
  }, [data?.floors, f?.floor]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetFilters = () => set({ q: "", project_id: "", floor: "", priority: "", status: "", type: "", date_from: "", date_to: "", assignee_id: "", category: "open" });
  const hiddenByFilter = data && data.total === 0 && (data.counts?.open || 0) > 0;

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const cats = tab === "team" ? TEAM_CATEGORIES : TASK_CATEGORIES;

  return (
    <div className="space-y-5" data-testid="my-tasks-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><ListChecks className="h-8 w-8 text-primary" />Meine Aufgaben</h1>
            <PageHelp id="aufgaben" title="Meine Aufgaben" text="Alle Ihnen zugewiesenen Mängel, Tickets, Prüfungen und Wartungen an einem Ort. Kennzahlen anklicken filtert die Liste. Ein Klick auf eine Aufgabe öffnet die Bearbeitung." />
          </div>
          <p className="text-slate-500 mt-1">{tab === "team" ? "Aufgaben aller Mitarbeiter in Ihren freigegebenen Objekten." : "Ihre zugewiesenen Vorgänge aus Mängeln, Tickets, Prüfungen und Wartungen."}</p>
        </div>
        {isManager && (
          <Tabs value={tab} onValueChange={switchTab}>
            <TabsList data-testid="tasks-tabs">
              <TabsTrigger value="mine" data-testid="tasks-tab-mine">Meine Aufgaben</TabsTrigger>
              <TabsTrigger value="team" data-testid="tasks-tab-team">Teamaufgaben</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {f && <TaskKpis counts={data?.counts} active={f.category} onPick={(c) => set({ category: c })} team={tab === "team"} doneDays={f.done_days} />}
      {tab === "team" && <TeamSummary data={team} selected={f?.assignee_id} onPickMember={(id) => set({ assignee_id: id, category: id === "__none" ? "open" : f.category })} />}
      {f && <TaskFilters f={f} set={set} categories={cats} projects={projects} floors={data?.floors || []} members={members} team={tab === "team"} />}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between text-sm">
          <span className="text-slate-600" data-testid="task-total">{data ? `${data.total} Vorgänge` : "…"}</span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2" data-testid="task-pagination">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="task-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-xs text-slate-500">Seite {page} / {totalPages}</span>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="task-page-next"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
        {hiddenByFilter && (
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-sm text-amber-800 flex items-center justify-between gap-3 flex-wrap" data-testid="tasks-hidden-by-filter">
            <span>{data.counts.open} offene Aufgabe(n) sind durch die aktuellen Filter ausgeblendet.</span>
            <Button size="sm" variant="outline" onClick={resetFilters} data-testid="tasks-reset-filters">Filter zurücksetzen</Button>
          </div>
        )}
        <TaskList items={data?.items || []} today={data?.today} loading={loading && !data} error={error} onOpen={(t) => setOpenRef({ type: t.type, id: t.id })}
          emptyText={tab === "team" ? "Keine Vorgänge für diese Auswahl." : "Keine Aufgaben in dieser Ansicht. Ihnen zugewiesene Mängel, Tickets und Prüfungen erscheinen hier automatisch."} />
      </div>

      {openRef && <TaskDetailDialog taskRef={openRef} onClose={closeDetail} onChanged={load} />}
    </div>
  );
}
